import React, { useState } from 'react';
import axios from 'axios';
import { Check, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WaitlistForm = ({ serviceName }) => {
  const [form, setForm] = useState({ name: '', email: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API}/waitlist`, {
        name: form.name.trim(),
        email: form.email.trim(),
        service: serviceName,
        note: form.note.trim(),
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`waitlist-success-${serviceName.replace(/\s+/g, '-').toLowerCase()}`}
        className="bg-white border border-[#c9a961]/40 p-8 md:p-10 text-center"
      >
        <div className="w-12 h-12 rounded-full bg-[#c9a961]/15 border border-[#c9a961] flex items-center justify-center mx-auto mb-4">
          <Check size={22} className="text-[#c9a961]" strokeWidth={2} />
        </div>
        <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-semibold mb-3">YOU&rsquo;RE ON THE LIST</p>
        <h3
          className="font-serif text-3xl text-[#1c1e20] mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          Thank you{form.name ? `, ${form.name.split(' ')[0]}` : ''}.
        </h3>
        <div className="w-12 h-[2px] bg-[#c9a961] mx-auto mb-5" />
        <p className="text-[#4a4a4a] leading-relaxed text-[15px] max-w-lg mx-auto">
          We&rsquo;ve added <strong>{form.email}</strong> to the priority list for {serviceName}.
          You&rsquo;ll be among the first to hear the moment this concierge service launches.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`waitlist-form-${serviceName.replace(/\s+/g, '-').toLowerCase()}`}
      className="bg-white border border-black/10 p-8 md:p-10"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={14} className="text-[#c9a961]" strokeWidth={1.6} />
        <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-semibold">JOIN THE PRIORITY LIST</p>
      </div>
      <h3
        className="font-serif text-2xl md:text-3xl text-[#1c1e20] mt-1 mb-2"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
      >
        Be the first to know.
      </h3>
      <p className="text-[#4a4a4a] leading-relaxed text-[14px] mb-6">
        Drop your email and we&rsquo;ll invite you personally the moment {serviceName} is live.
        No spam, no marketing lists &mdash; only a single launch note from our team.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Full name (optional)"
          data-testid="waitlist-name-input"
          className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961]"
        />
        <input
          required
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email address"
          data-testid="waitlist-email-input"
          className="border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961]"
        />
      </div>
      <textarea
        value={form.note}
        onChange={(e) => setForm({ ...form, note: e.target.value })}
        placeholder="Anything specific you're hoping for? (optional)"
        rows={2}
        data-testid="waitlist-note-input"
        className="w-full border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961] mb-4 resize-none"
      />

      {error && (
        <p className="text-rose-600 text-sm mb-3" data-testid="waitlist-error">{error}</p>
      )}

      <button
        type="submit"
        disabled={busy}
        data-testid="waitlist-submit"
        className="w-full md:w-auto bg-[#c9a961] hover:bg-[#b89751] transition-colors text-[#141618] tracking-[0.22em] text-[11px] font-semibold px-8 py-3.5 disabled:opacity-60"
      >
        {busy ? 'SAVING\u2026' : 'NOTIFY ME AT LAUNCH'}
      </button>
      <p className="text-[11px] text-[#6b6b6b] mt-4">
        By joining, you agree to receive one launch email for {serviceName}. Unsubscribe at any reply.
      </p>
    </form>
  );
};

export default WaitlistForm;
